import * as y from "yup";
import type { AudienceData } from "@common/interfaces/KYDMarketing";
import { hasInterestTargetingSignal } from "@common/utils/interestAudiences";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import { scheduleInputToInstant } from "@common/utils/adsetSchedule";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { FacebookPage, InstagramAccount } from "../types";
import { validateScheduledBudgetForm } from "../utils/validateScheduledBudgetForm";
import {
  hasMixedGuidedCreativeMediaTypes,
  MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
} from "../utils/validateCreativeMediaType";

dayjs.extend(utc);
dayjs.extend(timezone);

export const PRIMARY_TEXT_MAX = 1000;

type FlowValidationContext = {
  requireAudienceSection?: boolean;
  requireBudgetSection?: boolean;
  /** IANA zone for schedule wall times (start_at / end_at) */
  eventTimezone?: string;
  /** Minimum allowed average daily budget in major units (e.g. USD). */
  minAverageDailyBudget?: number;
};

const shouldRequireAudienceSection = (
  context: FlowValidationContext | undefined
) => context?.requireAudienceSection !== false;

const shouldRequireBudgetSection = (
  context: FlowValidationContext | undefined
) => context?.requireBudgetSection !== false;

/** Loose shapes for create-flow budget/schedule (matches form values). */
type CreateFlowBudgetShape = {
  type?: string | null;
  segments?: Array<{
    time_start?: string | null;
    time_end?: string | null;
    amount?: { amount?: unknown; currency?: string | null } | null;
  }> | null;
  amount?: { amount?: unknown; currency?: string | null } | null;
};

type CreateFlowScheduleShape = {
  start_at?: string | null;
  end_at?: string | null;
} | null | undefined;

export type CreateFlowScheduledBudgetPayloadError = {
  /** When set, use as full RHF path (e.g. `schedule.start_at`). */
  fullPath?: string;
  /** Path segment under `budget` (e.g. `segments[0].time_start`). */
  pathSuffix?: string;
  message: string;
};

/**
 * Scheduled-only: phases (times, amounts, contiguity) plus flight vs campaign schedule.
 * Caller must ensure `budget.type === "scheduled"`.
 */
export function getCreateFlowScheduledBudgetPayloadError(params: {
  budget: CreateFlowBudgetShape;
  schedule: CreateFlowScheduleShape;
  context: FlowValidationContext | undefined;
}): CreateFlowScheduledBudgetPayloadError | null {
  const { budget, schedule, context } = params;
  const segs = budget?.segments ?? [];
  const tz =
    context?.eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;

  const schedStart = String(schedule?.start_at ?? "").trim();
  const schedEnd = String(schedule?.end_at ?? "").trim();

  const formError = validateScheduledBudgetForm({
    segments: segs,
    eventTimezone: tz,
    deliveryWindow: {
      scheduleStartAt: schedStart,
      scheduleEndAt: schedEnd,
    },
    requireUtcDeliveryWindow: true,
    missingUtcDeliveryWindow: {
      fullPath: "schedule.start_at",
      message:
        "Campaign run dates are required so scheduled budgets can be checked against the ad delivery window.",
    },
  });
  if (formError) return formError;

  const minAverageDaily = context?.minAverageDailyBudget;
  if (typeof minAverageDaily === "number" && minAverageDaily > 0) {
    const startInstant = scheduleInputToInstant(
      String(schedule?.start_at ?? "").trim(),
      tz
    );
    const endInstant = scheduleInputToInstant(
      String(schedule?.end_at ?? "").trim(),
      tz
    );
    const totalHours =
      startInstant && endInstant ? endInstant.diff(startInstant, "hour", true) : 0;
    if (Number.isFinite(totalHours) && totalHours > 0) {
      const totalAmount = segs.reduce((sum, seg) => {
        const raw = seg?.amount?.amount;
        const value =
          raw != null && String(raw).trim() !== ""
            ? Number(String(raw).trim())
            : NaN;
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      const averageDailyAmount = totalAmount / (totalHours / 24);
      if (averageDailyAmount < minAverageDaily) {
        return {
          pathSuffix: "segments",
          message: `Scheduled budget average per 24h must be at least ${minAverageDaily.toFixed(
            2
          )}`,
        };
      }
    }
  }

  return null;
}

/**
 * Human-readable reasons budget + campaign schedule fail the create-flow gate (same rules as
 * {@link isCreateFlowBudgetSchedulePayloadComplete}). Surfaces scheduled-phase / flight issues
 * when Yup or RHF state is hard to read after add/remove segment churn.
 */
export function getCreateFlowBudgetScheduleIncompleteReasons(params: {
  budget: CreateFlowBudgetShape | undefined;
  schedule: CreateFlowScheduleShape;
  context: FlowValidationContext | undefined;
}): string[] {
  if (!shouldRequireBudgetSection(params.context)) return [];
  const { budget, schedule, context } = params;
  const reasons: string[] = [];

  if (!String(schedule?.start_at ?? "").trim()) {
    reasons.push("Campaign start date is required.");
  }
  if (!String(schedule?.end_at ?? "").trim()) {
    reasons.push("Campaign end date is required.");
  }

  const tz =
    context?.eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  const schedStart = String(schedule?.start_at ?? "").trim();
  const schedEnd = String(schedule?.end_at ?? "").trim();
  if (schedStart && schedEnd) {
    const ds = scheduleInputToInstant(schedStart, tz);
    const de = scheduleInputToInstant(schedEnd, tz);
    if (!ds || !de || !de.isAfter(ds)) {
      reasons.push("Campaign end must be after the campaign start.");
    }
  }

  if (!budget?.type) {
    reasons.push("Choose a budget type (daily, lifetime, or scheduled).");
  } else if (budget.type === "scheduled") {
    const scheduledErr = getCreateFlowScheduledBudgetPayloadError({
      budget,
      schedule,
      context,
    });
    if (scheduledErr?.message?.trim()) {
      reasons.push(scheduledErr.message.trim());
    }
  } else {
    const raw = budget.amount?.amount;
    const n =
      raw != null && raw !== ""
        ? Number(raw as string | number)
        : NaN;
    if (!Number.isFinite(n) || n <= 0) {
      reasons.push("Enter a budget amount greater than 0.");
    } else if (
      budget.type === "daily" &&
      typeof context?.minAverageDailyBudget === "number" &&
      context.minAverageDailyBudget > 0 &&
      n < context.minAverageDailyBudget
    ) {
      reasons.push(
        `Daily budget must be at least ${context.minAverageDailyBudget.toFixed(2)}.`
      );
    }
  }

  return Array.from(new Set(reasons));
}

/**
 * Whether budget + campaign schedule satisfy create-flow rules (mirrors Yup). Use for UI
 * completion and the “Review campaign” gate so we do not rely solely on `formState.isValid`.
 */
export function isCreateFlowBudgetSchedulePayloadComplete(params: {
  budget: CreateFlowBudgetShape | undefined;
  schedule: CreateFlowScheduleShape;
  context: FlowValidationContext | undefined;
}): boolean {
  return getCreateFlowBudgetScheduleIncompleteReasons(params).length === 0;
}

/**
 * When Smart Targeting is off, the flow still needs some manual narrowing:
 * custom audiences, interests, geo, non-default demographics, or enabled warm bundles.
 */
export function hasManualAudienceTargetingSignal(params: {
  custom_audiences?: readonly AudienceData[] | null;
  interests?: readonly unknown[] | null;
  countries?: readonly unknown[] | null;
  geo_markets?: readonly unknown[] | null;
  regions?: readonly unknown[] | null;
  cities?: readonly unknown[] | null;
  age_min?: number | null;
  age_max?: number | null;
  genders?: string | null;
  enabled_strategy_ids?: readonly string[] | null;
}): boolean {
  if ((params.enabled_strategy_ids?.length ?? 0) > 0) return true;
  if ((params.custom_audiences?.length ?? 0) > 0) return true;
  if (
    hasInterestTargetingSignal({
      custom_audiences: params.custom_audiences,
      interests: params.interests,
    })
  ) {
    return true;
  }
  if (
    (params.countries?.length ?? 0) > 0 ||
    (params.geo_markets?.length ?? 0) > 0 ||
    (params.regions?.length ?? 0) > 0 ||
    (params.cities?.length ?? 0) > 0
  ) {
    return true;
  }
  if (params.age_min != null && params.age_min !== 18) return true;
  if (params.age_max != null && params.age_max !== 65) return true;
  const g = params.genders;
  if (g != null && g !== "" && g !== "all") return true;
  return false;
}

function resolveCreativeSlotState(value: {
  squareMediaUrl?: string | null;
  squareMediaFileKey?: string | null;
  squareMediaType?: string | null;
  verticalMediaUrl?: string | null;
  verticalMediaFileKey?: string | null;
  verticalMediaType?: string | null;
}): {
  hasSquareAsset: boolean;
  hasSquareType: boolean;
  hasVerticalAsset: boolean;
  hasVerticalType: boolean;
} {
  return {
    hasSquareAsset: Boolean(value?.squareMediaUrl || value?.squareMediaFileKey),
    hasSquareType: Boolean(value?.squareMediaType),
    hasVerticalAsset: Boolean(
      value?.verticalMediaUrl || value?.verticalMediaFileKey
    ),
    hasVerticalType: Boolean(value?.verticalMediaType),
  };
}

const creativeSchema = y
  .object({
    name: y.string().optional(),
    squareMediaUrl: y.string().nullable().optional(),
    squareMediaFileKey: y.string().nullable().optional(),
    squareMediaType: y.string().oneOf(["image", "video"]).nullable().optional(),
    verticalMediaUrl: y.string().nullable().optional(),
    verticalMediaFileKey: y.string().nullable().optional(),
    verticalMediaType: y
      .string()
      .oneOf(["image", "video"])
      .nullable()
      .optional(),
    primaryText: y
      .string()
      .required("Primary text is required")
      .max(
        PRIMARY_TEXT_MAX,
        `Primary text must be at most ${PRIMARY_TEXT_MAX} characters`
      ),
    fbPage: y
      .mixed<FacebookPage | null>()
      .nullable()
      .test("fb-page-required", "Facebook Page is required", (value) =>
        Boolean(value?.id)
      ),
    instagramAccount: y
      .mixed<InstagramAccount | null>()
      .nullable()
      .optional(),
  })
  .test(
    "creative-at-least-one-media-slot",
    "At least one creative media format is required",
    (value) => {
      const {
        hasSquareAsset,
        hasSquareType,
        hasVerticalAsset,
        hasVerticalType,
      } = resolveCreativeSlotState(value ?? {});
      return (
        (hasSquareAsset && hasSquareType) ||
        (hasVerticalAsset && hasVerticalType)
      );
    }
  )
  .test(
    "creative-media-types-must-match",
    MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
    (value) => !hasMixedGuidedCreativeMediaTypes(value)
  );

export const CreateCampaignFlowSchema = y.object({
  creatives: y
    .array()
    .of(creativeSchema)
    .min(1, "At least one creative is required")
    .test(
      "creatives-instagram-uniform-across-adset",
      "If any creative uses an Instagram account, every creative in this ad set must choose one (each can use a different account).",
      (creatives) => {
        if (!Array.isArray(creatives) || creatives.length === 0) return true;
        const hasInstagram = (row: unknown) =>
          String(
            (row as { instagramAccount?: { id?: string } | null })?.instagramAccount
              ?.id ?? ""
          ).trim() !== "";
        if (!creatives.some(hasInstagram)) return true;
        return creatives.every(hasInstagram);
      }
    )
    .required(),
  smart_targeting: y.boolean().required(),
  custom_audiences: y
    .array()
    .of(y.mixed<AudienceData>().required())
    .test(
      "manual-targeting-signal-when-visible",
      "When Smart Targeting is disabled, add at least one interest, location, custom audience, or enabled strategy",
      function (value) {
        const context = this.options.context as
          | FlowValidationContext
          | undefined;
        if (!shouldRequireAudienceSection(context)) return true;
        if (this.parent.smart_targeting) return true;
        const parent = this.parent as {
          interests?: { key?: string }[];
          countries?: unknown[];
          geo_markets?: unknown[];
          regions?: unknown[];
          cities?: unknown[];
          age_min?: number | null;
          age_max?: number | null;
          genders?: string | null;
          enabled_strategy_ids?: string[];
        };
        return hasManualAudienceTargetingSignal({
          custom_audiences: value,
          interests: parent.interests,
          countries: parent.countries,
          geo_markets: parent.geo_markets,
          regions: parent.regions,
          cities: parent.cities,
          age_min: parent.age_min,
          age_max: parent.age_max,
          genders: parent.genders,
          enabled_strategy_ids: parent.enabled_strategy_ids,
        });
      }
    ),
  audience_search_query: y.string().default(""),
  // Targeting fields (used when smart_targeting is false) — locations, age/gender, interests
  countries: y
    .array()
    .of(
      y.object({
        key: y.string().nullable(),
        value: y.string().nullable(),
      })
    )
    .default([]),
  geo_markets: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        name: y.string(),
        country: y.string(),
        value: y.string().required(),
      })
    )
    .default([]),
  regions: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        value: y.string().required(),
      })
    )
    .default([]),
  cities: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        value: y.string().required(),
      })
    )
    .default([]),
  age_min: y.number().nullable().default(18),
  age_max: y.number().nullable().default(65),
  genders: y.string().nullable().default("all"),
  interests: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        value: y.string().required(),
      })
    )
    .default([]),
  enabled_strategy_ids: y.array().of(y.string().required()).default([]),
  targeting_query: y.string().default(""),
  targeting_subtype: y
    .object({
      value: y.string(),
      label: y.string(),
    })
    .nullable()
    .default(null),
  budget: y
    .object({
      type: y
        .string()
        .oneOf(["daily", "lifetime", "scheduled"])
        .test(
          "budget-type-required-when-visible",
          "Budget type is required",
          function (value) {
            const context = this.options.context as
              | FlowValidationContext
              | undefined;
            if (!shouldRequireBudgetSection(context)) return true;
            return Boolean(value);
          }
        ),
      /**
       * Shape only for `amount.amount` / `base.amount.amount`: inputs may be number,
       * string, or null. Daily/lifetime rules live in `budget-amount-or-scheduled`.
       */
      amount: y.object({
        amount: y.mixed().nullable().optional(),
        /** Unused for `scheduled`; must not be required or Yup blocks publish. */
        currency: y.string().optional(),
      }),
      base: y
        .object({
          type: y.string().oneOf(["daily", "lifetime"]).optional(),
          amount: y.object({
            amount: y.mixed().nullable().optional(),
            currency: y.string().optional(),
          }),
        })
        .optional(),
      /** Shape only; full scheduled rules live in `budget-amount-or-scheduled` (RHF + HTML inputs). */
      segments: y
        .array()
        .of(
          y.object({
            time_start: y.string().optional(),
            time_end: y.string().optional(),
            amount: y
              .object({
                amount: y.mixed().nullable().optional(),
                currency: y.string().optional(),
              })
              .optional(),
          })
        )
        .default([]),
    })
    .test(
      "budget-amount-or-scheduled",
      "Budget amount must be greater than 0",
      function (value) {
        const context = this.options.context as
          | FlowValidationContext
          | undefined;
        if (!shouldRequireBudgetSection(context)) return true;
        if (!value?.type) return false;
        if (value.type === "scheduled") {
          const schedule = this.parent.schedule;
          const err = getCreateFlowScheduledBudgetPayloadError({
            budget: value,
            schedule,
            context,
          });
          if (err) {
            return this.createError({
              path: err.fullPath ?? `${this.path}.${err.pathSuffix}`,
              message: err.message,
            });
          }
          return true;
        }
        const raw = value.amount?.amount;
        const n =
          raw != null && raw !== ""
            ? Number(raw as string | number)
            : NaN;
        if (!Number.isFinite(n) || n <= 0) {
          return this.createError({
            path: `${this.path}.amount.amount`,
            message: "Budget amount must be greater than 0",
          });
        }
        if (
          value.type === "daily" &&
          typeof context?.minAverageDailyBudget === "number" &&
          context.minAverageDailyBudget > 0 &&
          n < context.minAverageDailyBudget
        ) {
          return this.createError({
            path: `${this.path}.amount.amount`,
            message: `Daily budget must be at least ${context.minAverageDailyBudget.toFixed(
              2
            )}`,
          });
        }
        return true;
      }
    ),
  schedule: y.object({
    start_at: y
      .string()
      .test(
        "schedule-start-required-when-visible",
        "Start date is required",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          return Boolean(value);
        }
      ),
    end_at: y
      .string()
      .test(
        "schedule-end-required-when-visible",
        "End date is required",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          return Boolean(value);
        }
      )
      .test(
        "end-after-start-when-visible",
        "End date must be after start date",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          const startAt = this.parent.start_at;
          if (!startAt || !value) return false;
          const tz =
            context?.eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
          const startInst = scheduleInputToInstant(String(startAt).trim(), tz);
          const endInst = scheduleInputToInstant(String(value).trim(), tz);
          return Boolean(
            startInst && endInst && endInst.isAfter(startInst)
          );
        }
      ),
  }),
});

export type CreateCampaignFlowValues = y.InferType<
  typeof CreateCampaignFlowSchema
>;
